import React, { useState, FC, useMemo } from 'react';
import { ModalSizeQuantities } from 'src/components/BundleProduct/ModalSizeQuantities';
import { swatchCss } from 'src/components/ProductDetail/utils/swatchData';
import { ContextType as BundleOptions } from 'src/components/BundleProduct/BundleOptionsProvider';
import { Size as BundleSize } from 'src/components/ProductDetail/ProductSizes';
import { appendStockToSize } from 'src/components/ProductDetail/utils/selectVariantsFromOptions';
import { MediaEntry } from 'src/types/global-types';
import { ColorAndSize, Size } from 'src/components/BundleProduct/types';

import { Swatch } from '../ProductDetail/ProductSwatch';

import { StepContainer } from './StepContainer';
import classes from './BundleSizesSelector.scss';
import { BundleButton } from './BundleButton';
import { DoubleButton } from './DoubleButton';
import { Tag } from './Tag';
import { CloseButton } from './CloseButton';

type Props = Pick<BundleOptions, 'variants'> & {
    sizes: BundleSize[];
    colorAndSize: ColorAndSize;
    onSubmit: (values: ColorAndSize) => void;
    swatches: Swatch[];
    onDelete: (Swatch) => void;
    sizeGuide: string;
    mediaEntry?: MediaEntry;
    maxQty: number;
    currentQty: number;
};

const filterData = (color: number, swatches: Swatch[]) => {
    const filtered = swatches.filter((swatch) => swatch.value_index === color);

    return filtered[0];
};

export const createSwatchTitle = (title: string) => {
    const [partOne, partTwo] = title.split('/');

    if (partTwo) {
        return `${partOne} / ${partTwo}`.toLowerCase();
    }

    return partOne.toLowerCase();
};

export const BundleSizesSelector: FC<Props> = ({
    sizes,
    colorAndSize,
    onSubmit,
    swatches,
    onDelete,
    variants,
    sizeGuide,
    mediaEntry,
    maxQty,
    currentQty,
}) => {
    const [open, setOpen] = useState(false);

    const computedSizes: BundleSize[] = useMemo(() => {
        const output: BundleSize[] = [];
        sizes.forEach((size) => {
            const colorObject = {
                color: colorAndSize.colorId,
            };
            const next = appendStockToSize(size, colorObject, variants);
            if (next.simpleProductId !== -1) {
                output.push(next);
            }
        });
        return output;
    }, [colorAndSize.colorId, sizes, variants]);

    const handleSubmit = (sizes: Size[]) =>
        onSubmit({
            ...colorAndSize,
            sizes: sizes.filter((size) => size.quantity > 0),
        });

    const initialValues = {
        sizes: [
            ...computedSizes.map((size) => {
                const currentValue = colorAndSize.sizes.find((value) => value.id === size.id);
                return {
                    id: Number(size.id),
                    quantity: currentValue?.quantity || 0,
                };
            }),
        ],
    };

    const createSizes = (id, quantity) => {
        const filtered = computedSizes.filter((size) => size.id === id);
        const name = filtered[0].title;

        return `${name} x ${parseInt(quantity)} pcs`;
    };

    const getCurrentColorQty = () => {
        let qty = 0;

        colorAndSize.sizes.map((size) => (qty += size.quantity));

        return qty;
    };

    const swatch = filterData(colorAndSize.colorId, swatches);
    const css = swatchCss(swatch.swatch_data);
    const currentColorQty = getCurrentColorQty();

    return (
        <StepContainer>
            <div className={classes.infoContainer}>
                <div className={classes.swatchContainer}>
                    <span className={classes.swatchesItem} style={css}>
                        {currentColorQty > 0 && (
                            <span className={classes.qtyCountContainer}>
                                <span className={classes.qtyCount}>{currentColorQty}</span>
                            </span>
                        )}
                    </span>
                    <span className={classes.title}>{createSwatchTitle(swatch.title)}</span>
                </div>

                <CloseButton onClick={() => onDelete(swatch)} background="gray" />
                <div className={classes.sizeContainer}>
                    {colorAndSize.sizes.map((size) => {
                        return <Tag key={size.id}>{createSizes(size.id, size.quantity)}</Tag>;
                    })}
                </div>
            </div>

            <div className={classes.container}>
                {colorAndSize.sizes.length === 0 ? (
                    <BundleButton
                        icon="v-right.svg"
                        iconOnRight
                        background="orange"
                        onClick={() => setOpen(true)}
                        size="big"
                        type="button"
                        bold
                    >
                        Select quantity & sizes
                    </BundleButton>
                ) : (
                    <DoubleButton
                        background="white"
                        onclick={() => setOpen(true)}
                        secondaryOnClick={() => onDelete(swatch)}
                        text="Edit"
                        secondaryText="Delete"
                        icon="edit.svg"
                        secondaryIcon="delete.svg"
                        dimensions="big"
                    />
                )}
            </div>
            {open && (
                <ModalSizeQuantities
                    title={createSwatchTitle(swatch.title)}
                    isOpenSizeQuantitiesModal={open}
                    closeSizeQuantitiesModal={() => setOpen(false)}
                    colorId={colorAndSize.colorId}
                    initialValues={initialValues}
                    handleSubmit={handleSubmit}
                    sizes={computedSizes}
                    swatchCss={css}
                    sizeGuide={sizeGuide}
                    imageUrl={mediaEntry?.large_url}
                    maxQty={maxQty}
                    currentQty={currentQty}
                />
            )}
        </StepContainer>
    );
};
